import type { User as DomainUser } from '@domain/schemas/database.schema';
import type { BaseUser, Team, User } from '@root/@types/types';
import { UserRole } from '@root/@types/types';
import { tenantConfig } from '@root/config/tenants';
import { get } from 'lodash-es';

import { isNonNullish } from '../isNonNullish';

export const getUserRole = (user: User | BaseUser | DomainUser | null): UserRole | undefined =>
  get(user, 'attributes["custom:role"]') ?? (get(user, 'role') as UserRole);
const TEAMS = tenantConfig.teams ?? [];

export const getTeamNamesByTeamIds = (teamIds: number[]): string[] =>
  teamIds.map((teamId: number) => TEAMS?.find((team) => team.id === teamId)?.name ?? '').filter((i: string) => i);

export const getUserTeamNames = (user: BaseUser | null, showAllIfUserNotAssignedToAny = false): string[] => {
  const tenantIds = user?.tenantIds ?? [];
  const userTeams = getTeamNamesByTeamIds(tenantIds);

  if (showAllIfUserNotAssignedToAny && userTeams.length === 0) {
    return TEAMS.map((team) => team.name);
  }

  return userTeams;
};

export const getUserTeams = (user: BaseUser | null, showAllIfUserNotAssignedToAny = false): Team[] => {
  const tenantIds = user?.tenantIds ?? [];
  const userTeams = tenantIds.map((teamId: number) => TEAMS?.find((team) => team.id === teamId)).filter(isNonNullish);

  if (showAllIfUserNotAssignedToAny && userTeams.length === 0) {
    return TEAMS;
  }

  return userTeams;
};

export const getAllTeams = (): Team[] => TEAMS;

export const ADMIN_ROLES = [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager];

/**
 *
 * @param user - BaseUser object
 * @param matchStrict - if true, only UserRole.Admin will be considered as an admin
 */
export const isAdmin = (user: User | BaseUser | DomainUser | null, matchStrict: boolean = false) => {
  const role = getUserRole(user);

  if (!role) {
    return false;
  }

  if (matchStrict) {
    return role === UserRole.Admin;
  }

  return ADMIN_ROLES.includes(role);
};

export const isSuperadmin = (user: User | BaseUser | null) => {
  const role = getUserRole(user);
  return role === UserRole.Superadmin;
};

export const isTemplateManager = (user: User | BaseUser | null) => {
  const role = getUserRole(user);
  return role === UserRole.TemplateManager;
};
